import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    getContentPaddingStyle,
    loadNativeWebViewInsets,
    NativeWebViewInsets,
    parseNativeWebViewInsetsFromSearch,
    subscribeNativeInsetsUpdates,
    updateNativeWebViewInsets,
} from "../utils/nativeWebViewInsets";

type NativeWebViewInsetsContextValue = NativeWebViewInsets & {
    contentPaddingStyle: Record<string, string>;
    contentPaddingTopStyle: Record<string, string>;
    contentPaddingBottomStyle: Record<string, string>;
    contentPaddingHorizontalStyle: Record<string, string>;
};

const NativeWebViewInsetsContext = createContext<NativeWebViewInsetsContextValue | null>(
    null
);

export function useNativeWebViewInsets(): NativeWebViewInsetsContextValue {
    const context = useContext(NativeWebViewInsetsContext);
    if (!context) {
        throw new Error("useNativeWebViewInsets must be used within NativeWebViewInsetsProvider");
    }
    return context;
}

interface Props {
    children: ReactNode;
}

export function NativeWebViewInsetsProvider({ children }: Props) {
    const [insets, setInsets] = useState<NativeWebViewInsets>(() => loadNativeWebViewInsets());

    useEffect(() => {
        if (typeof window === "undefined") return;

        const fromUrl = parseNativeWebViewInsetsFromSearch(window.location.search);
        if (fromUrl.isNativeWebView) {
            const next = updateNativeWebViewInsets({
                paddingTop: fromUrl.paddingTop,
                paddingBottom: fromUrl.paddingBottom,
                paddingLeft: fromUrl.paddingLeft,
                paddingRight: fromUrl.paddingRight,
                appOS: fromUrl.appOS,
            });
            setInsets(next);
        }
    }, []);

    useEffect(() => {
        return subscribeNativeInsetsUpdates(setInsets);
    }, []);

    const value = useMemo<NativeWebViewInsetsContextValue>(
        () => ({
            ...insets,
            contentPaddingStyle: getContentPaddingStyle(insets),
            contentPaddingTopStyle: getContentPaddingStyle(insets, {
                bottom: false,
                left: false,
                right: false,
            }),
            contentPaddingBottomStyle: getContentPaddingStyle(insets, {
                top: false,
                left: false,
                right: false,
            }),
            contentPaddingHorizontalStyle: getContentPaddingStyle(insets, {
                top: false,
                bottom: false,
            }),
        }),
        [insets]
    );

    return (
        <NativeWebViewInsetsContext.Provider value={value}>
            {children}
        </NativeWebViewInsetsContext.Provider>
    );
}
